import { useState, useEffect } from 'react';
import { Settings, Database, Mail, Key, Shield, Server, RefreshCw, Tag, Plus, Trash2, Edit2, Filter, Lock, AlertTriangle, Bell, Network, Plug, Sun } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'react-toastify';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { IconPicker } from '@/components/settings/IconPicker';
import { AuditLogLink } from '@/components/settings/AuditLogLink';
import { createAdminLabel, updateAdminLabel, deleteAdminLabel } from '@/lib/adminApi';
import { useSettingsContext } from '@/contexts/SettingsContext';
import type { IndustryLabel } from '@/contexts/SettingsContext';
import { getIconComponent } from '@/lib/iconUtils';
import { RateLimitPanel } from '@/components/settings/RateLimitPanel';
import { SuspiciousIPsPanel } from '@/components/settings/SuspiciousIPsPanel';
import { CountryBlockPanel } from '@/components/settings/CountryBlockPanel';
import { IPWhitelistPanel } from '@/components/settings/IPWhitelistPanel';
import { AutoBlockSettings } from '@/components/settings/AutoBlockSettings';
import { AnomalyDetectionSettings } from '@/components/settings/AnomalyDetectionSettings';
import { SolArkSyncPanel } from '@/components/settings/SolArkSyncPanel';
// SystemAlertsPanel + AlertRecipientsPanel moved to src/pages/settings/AlertsSettings.tsx
// (their own top-level /settings/alerts section — un-nested from System Preferences).
import { UnitFreshnessCard } from '@/components/settings/UnitFreshnessCard';

export function SettingsConfig() {
  const { labels: contextLabels, refreshLabels, systemConfig, updateSystemConfig } = useSettingsContext();
  // Use local state for labels so we can update them even when API fails
  const [labels, setLabels] = useState<IndustryLabel[]>(contextLabels);
  const [tableExists, setTableExists] = useState<boolean | null>(null); // null = unknown, true/false = known
  const [apiKey, setApiKey] = useState('re_123456789');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<IndustryLabel | null>(null);
  const [formData, setFormData] = useState<{
    industry: string;
    label_type: 'group' | 'unit' | 'site' | 'team';
    label_value: string;
    icon: string | null;
    superadmin_override: boolean;
    admin_override: boolean;
  }>({
    industry: '',
    label_type: 'group',
    label_value: '',
    icon: null,
    superadmin_override: false,
    admin_override: false
  });

  // Sync local labels with context labels when they change
  useEffect(() => {
    if (contextLabels.length > 0) {
      setLabels(contextLabels);
      // If we have labels from context, table likely exists
      if (tableExists === null) {
        setTableExists(true);
      }
    } else if (contextLabels.length === 0 && labels.length > 0) {
      // Keep local labels if context is empty (might be loading)
      // Don't clear local state unnecessarily
    }
  }, [contextLabels]);

  const handleAddLabel = () => {
    setEditingLabel(null);
    setFormData({ industry: '', label_type: 'group', label_value: '', icon: null, superadmin_override: false, admin_override: false });
    setDrawerOpen(true);
  };

  const handleEditLabel = (label: IndustryLabel) => {
    setEditingLabel(label);
    setFormData({
      industry: label.industry,
      label_type: label.label_type,
      label_value: label.label_value,
      icon: label.icon || null,
      superadmin_override: label.superadmin_override || false,
      admin_override: label.admin_override || false
    });
    setDrawerOpen(true);
  };

  const handleDeleteLabel = async (id: string) => {
    if (!confirm('Are you sure you want to delete this label?')) return;

    try {
      await deleteAdminLabel(id);
      await refreshLabels(); // Refresh labels from context
      toast.success('Label deleted');
    } catch (error: unknown) {
      console.error('Failed to delete label:', error);
      toast.error((error as Error)?.message || 'Failed to delete label');
    }
  };

  const handleSubmitLabel = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingLabel) {
        const result = await updateAdminLabel(editingLabel.id, formData);
        // Update local state immediately with API response
        setLabels(prev => prev.map(l => l.id === editingLabel.id ? result.label : l));
        toast.success('Label updated');
      } else {
        const result = await createAdminLabel(formData);
        // Add new label to local state immediately
        setLabels(prev => [...prev, result.label]);
        toast.success('Label added');
      }
      // Refresh labels from context in background
      refreshLabels().catch(err => console.warn('Background refresh failed:', err));
      setDrawerOpen(false);
    } catch (error: unknown) {
      console.error('Failed to save label:', error);
      toast.error((error as Error)?.message || 'Failed to save label');
    }
  };

  const handleLabelOverrideChange = async (labelId: string, field: 'superadmin_override' | 'admin_override', checked: boolean) => {
    // Update local state immediately for responsive UI
    setLabels(prev => prev.map(l =>
      l.id === labelId ? { ...l, [field]: checked } : l
    ));

    // Try to save to API
    try {
      const result = await updateAdminLabel(labelId, { [field]: checked });
      // Update local state with the response from API
      setLabels(prev => prev.map(l =>
        l.id === labelId ? result.label : l
      ));
      // Refresh context in background
      refreshLabels().catch(err => console.warn('Background refresh failed:', err));
      setTableExists(true);
    } catch (error: unknown) {
      console.error(`Failed to update ${field}:`, error);
      if ((error as Error)?.message?.includes('industry_labels') || (error as Error)?.message?.includes('table')) {
        setTableExists(false);
        console.warn('Labels table does not exist yet. Changes are local only.');
      } else {
        toast.error((error as Error)?.message || 'Failed to update override');
        // Revert local state on unexpected error
        setLabels(prev => prev.map(l =>
          l.id === labelId ? { ...l, [field]: !checked } : l
        ));
      }
    }
  };

  return (
    // Inner-scroll layout is desktop-only; on mobile the page flows so the
    // settings <main> handles vertical scrolling
    <div className="space-y-6 md:h-full md:overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-white whitespace-nowrap">System Preferences</h1>
          <AuditLogLink resourceTypes={['system_settings']} tooltip="View settings audit history" />
        </div>
        <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
          Manage global platform settings and integrations.
        </p>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="general" className="flex-1 flex flex-col md:overflow-hidden">
        <TabsList className="bg-card border border-border p-1 w-fit">
          <TabsTrigger value="general" className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            <Settings className="w-4 h-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="labels" className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            <Tag className="w-4 h-4" />
            Labels
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            <Plug className="w-4 h-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="solark" className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            <Sun className="w-4 h-4" />
            Sol-Ark
          </TabsTrigger>
          {/* System Alerts moved to its own top-level Settings section (/settings/alerts). */}
          <TabsTrigger value="security" className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            <Shield className="w-4 h-4" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="flex-1 overflow-y-auto mt-6 pr-2">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* General Settings */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#8FB560]" />
                General Settings
              </h2>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>Platform Name</Label>
                  <Input defaultValue="Hollywood Trucks" className="bg-background border-border" />
                </div>
                <div className="space-y-2">
                  <Label>Support Email</Label>
                  <Input defaultValue="support@hollywoodtrucks.com" className="bg-background border-border" />
                </div>
                <div className="space-y-2">
                  <Label>Default Home Page</Label>
                  <Select
                    value={systemConfig.defaultHomePage}
                    onValueChange={(value: 'dashboard' | 'map') => updateSystemConfig({ defaultHomePage: value })}
                  >
                    <SelectTrigger className="bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="map">Map View</SelectItem>
                      <SelectItem value="dashboard">Dashboard (Energy Analytics)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Choose which page users see when they first log in.
                  </p>
                </div>
                <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                  <div>
                    <h3 className="text-sm font-medium text-white">Maintenance Mode</h3>
                    <p className="text-xs text-muted-foreground">Prevent non-admin users from accessing the system.</p>
                  </div>
                  <Switch />
                </div>
              </div>
            </div>

            {/* Filter Settings */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Filter className="w-5 h-5 text-[#8FB560]" />
                Filter Settings
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                  <div>
                    <h3 className="text-sm font-medium text-white">Auto-hide Industry Filter</h3>
                    <p className="text-xs text-muted-foreground">
                      Automatically hide the industry dropdown when only one industry has active units.
                      Superadmins always see the filter on the Reports page.
                    </p>
                  </div>
                  <Switch
                    checked={systemConfig.autoHideIndustryFilter}
                    onCheckedChange={(checked) => updateSystemConfig({ autoHideIndustryFilter: checked })}
                  />
                </div>
              </div>
            </div>

            <UnitFreshnessCard />
          </div>
        </TabsContent>

        {/* Labels Tab */}
        <TabsContent value="labels" className="flex-1 overflow-y-auto mt-6 pr-2">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Tag className="w-5 h-5 text-indigo-400" />
                  Industry Labels
                </h2>
                <p className="text-sm text-muted-foreground">Customize terminology for different industries.</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleAddLabel} className="border-border hover:bg-muted">
                <Plus className="w-4 h-4 mr-2" />
                Add Label
              </Button>
            </div>

            {tableExists === false && (
              <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm text-yellow-400">
                ⚠️ Labels table not found. Changes are local only and will not persist. Run the migration to create the <code className="text-yellow-300">industry_labels</code> table.
              </div>
            )}

            <div className="rounded-md border border-border overflow-hidden">
              <Table>
                <TableHeader className="bg-background">
                  <TableRow className="border-border hover:bg-background">
                    <TableHead className="text-muted-foreground">Industry</TableHead>
                    <TableHead className="text-muted-foreground">Type</TableHead>
                    <TableHead className="text-muted-foreground">Label</TableHead>
                    <TableHead className="text-muted-foreground">Icon</TableHead>
                    <TableHead className="text-center text-muted-foreground">Superadmin Override</TableHead>
                    <TableHead className="text-center text-muted-foreground">Admin Override</TableHead>
                    <TableHead className="text-right text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {labels.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No custom labels configured.
                      </TableCell>
                    </TableRow>
                  ) : (
                    labels.map((label) => {
                      const IconComponent = getIconComponent(label.icon);
                      return (
                        <TableRow key={label.id} className="border-border hover:bg-muted/50">
                          <TableCell className="font-medium text-white capitalize">{label.industry}</TableCell>
                          <TableCell className="text-foreground capitalize">{label.label_type}</TableCell>
                          <TableCell className="text-foreground">{label.label_value}</TableCell>
                          <TableCell className="text-foreground">
                            {IconComponent ? (
                              <div className="flex items-center gap-2">
                                <IconComponent className="w-4 h-4" />
                                <span className="text-xs text-muted-foreground">{label.icon}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">No icon</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={label.superadmin_override}
                                onCheckedChange={(checked) => handleLabelOverrideChange(label.id, 'superadmin_override', checked as boolean)}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={label.admin_override}
                                onCheckedChange={(checked) => handleLabelOverrideChange(label.id, 'admin_override', checked as boolean)}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => handleEditLabel(label)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400" onClick={() => handleDeleteLabel(label.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="flex-1 overflow-y-auto mt-6 pr-2">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Email Service */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-400" />
                Email Service (Resend)
              </h2>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="pl-9 bg-background border-border"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>From Address</Label>
                  <Input defaultValue="onboarding@hollywoodtrucks.com" className="bg-background border-border" />
                </div>
                <div className="pt-2">
                  <Button variant="outline" size="sm" className="w-full border-border">
                    <RefreshCw className="w-3 h-3 mr-2" /> Test Connection
                  </Button>
                </div>
              </div>
            </div>

            {/* Database & Storage */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-400" />
                Database & Storage
              </h2>
              <div className="space-y-4">
                <div className="p-4 bg-background rounded-lg border border-border flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-500/10 p-2 rounded text-purple-400">
                      <Database className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">PostgreSQL</p>
                      <p className="text-xs text-muted-foreground">Connected (v15.1)</p>
                    </div>
                  </div>
                  <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">Healthy</span>
                </div>
                <div className="p-4 bg-background rounded-lg border border-border flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-500/10 p-2 rounded text-orange-400">
                      <Server className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Redis Cache</p>
                      <p className="text-xs text-muted-foreground">Connected (Railway)</p>
                    </div>
                  </div>
                  <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">Healthy</span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Sol-Ark Tab */}
        <TabsContent value="solark" className="flex-1 overflow-y-auto mt-6 pr-2">
          <SolArkSyncPanel />
        </TabsContent>

        {/* System Alerts moved to its own top-level section at /settings/alerts. */}

        {/* Security Tab */}
        <TabsContent value="security" className="flex-1 overflow-y-auto mt-6 pr-2">
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-400" />
              Security Settings
            </h2>

            <Tabs defaultValue="authentication" className="w-full">
              <TabsList className="bg-card border border-border p-1 w-fit mb-6">
                <TabsTrigger value="authentication" className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
                  <Lock className="w-4 h-4" />
                  Authentication
                </TabsTrigger>
                <TabsTrigger value="anomaly" className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
                  <AlertTriangle className="w-4 h-4" />
                  Anomaly Detection
                </TabsTrigger>
                <TabsTrigger value="alerts" className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
                  <Bell className="w-4 h-4" />
                  Alerts
                </TabsTrigger>
                <TabsTrigger value="traffic" className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
                  <Network className="w-4 h-4" />
                  Traffic Control
                </TabsTrigger>
              </TabsList>

              {/* Authentication Tab */}
              <TabsContent value="authentication">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                    <div>
                      <h4 className="text-sm font-medium text-white">Enforce 2FA</h4>
                      <p className="text-xs text-muted-foreground">Require two-factor for all admins.</p>
                    </div>
                    <Switch />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                    <div>
                      <h4 className="text-sm font-medium text-white">Session Timeout (min)</h4>
                      <p className="text-xs text-muted-foreground">Auto-logout after inactivity.</p>
                    </div>
                    <Input type="number" defaultValue="30" className="bg-background border-border h-8 w-20 text-center" />
                  </div>
                </div>
              </TabsContent>

              {/* Anomaly Detection Tab */}
              <TabsContent value="anomaly">
                <AnomalyDetectionSettings />
              </TabsContent>

              {/* Alerts Tab */}
              <TabsContent value="alerts">
                <div className="rounded-lg border border-border overflow-x-auto">
                  <table className="w-full min-w-[480px]">
                    <thead className="bg-background">
                      <tr className="text-xs text-muted-foreground">
                        <th className="text-left p-3 font-medium">Alert Type</th>
                        <th className="text-center p-3 font-medium w-24">In-App Toast</th>
                        <th className="text-center p-3 font-medium w-24">Email</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr className="hover:bg-muted/30">
                        <td className="p-3 text-sm text-white">Failed Login Spike</td>
                        <td className="p-3 text-center"><Checkbox defaultChecked /></td>
                        <td className="p-3 text-center"><Checkbox /></td>
                      </tr>
                      <tr className="hover:bg-muted/30">
                        <td className="p-3 text-sm text-white">Geographic Anomaly</td>
                        <td className="p-3 text-center"><Checkbox defaultChecked /></td>
                        <td className="p-3 text-center"><Checkbox /></td>
                      </tr>
                      <tr className="hover:bg-muted/30">
                        <td className="p-3 text-sm text-white">Bulk Deletion</td>
                        <td className="p-3 text-center"><Checkbox defaultChecked /></td>
                        <td className="p-3 text-center"><Checkbox defaultChecked /></td>
                      </tr>
                      <tr className="hover:bg-muted/30">
                        <td className="p-3 text-sm text-white">Off-Hours Critical Action</td>
                        <td className="p-3 text-center"><Checkbox defaultChecked /></td>
                        <td className="p-3 text-center"><Checkbox /></td>
                      </tr>
                      <tr className="hover:bg-muted/30">
                        <td className="p-3 text-sm text-white">Suspicious IP Detected</td>
                        <td className="p-3 text-center"><Checkbox defaultChecked /></td>
                        <td className="p-3 text-center"><Checkbox defaultChecked /></td>
                      </tr>
                      <tr className="hover:bg-muted/30">
                        <td className="p-3 text-sm text-white">New Superadmin Login</td>
                        <td className="p-3 text-center"><Checkbox defaultChecked /></td>
                        <td className="p-3 text-center"><Checkbox defaultChecked /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  In-app toasts appear for superadmins. Email alerts are sent to all superadmin accounts.
                </p>
              </TabsContent>

              {/* Traffic Control Tab */}
              <TabsContent value="traffic">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="p-4 bg-background rounded-lg border border-border">
                    <RateLimitPanel />
                  </div>
                  <div className="p-4 bg-background rounded-lg border border-border">
                    <AutoBlockSettings />
                  </div>
                  <div className="p-4 bg-background rounded-lg border border-border">
                    <SuspiciousIPsPanel />
                  </div>
                  <div className="p-4 bg-background rounded-lg border border-border">
                    <IPWhitelistPanel />
                  </div>
                  <div className="lg:col-span-2 p-4 bg-background rounded-lg border border-border">
                    <CountryBlockPanel />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>
      </Tabs>

      <AdminFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingLabel ? "Edit Label" : "Add Industry Label"}
        description="Define custom terminology for specific industries."
      >
        <form onSubmit={handleSubmitLabel} className="space-y-4">
          <div className="space-y-2">
            <Label>Industry Key</Label>
            <Select
              value={formData.industry}
              onValueChange={val => setFormData({...formData, industry: val})}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="film">Film</SelectItem>
                <SelectItem value="construction">Construction</SelectItem>
                <SelectItem value="events">Events</SelectItem>
                <SelectItem value="sports">Sports</SelectItem>
                <SelectItem value="music">Music</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Label Type</Label>
            <Select
              value={formData.label_type}
              onValueChange={val => setFormData({...formData, label_type: val as 'group' | 'unit' | 'site' | 'team'})}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="group">Group Level</SelectItem>
                <SelectItem value="site">Site Level</SelectItem>
                <SelectItem value="team">Team Level</SelectItem>
                <SelectItem value="unit">Unit Level</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Display Value</Label>
            <Input
              value={formData.label_value}
              onChange={e => setFormData({...formData, label_value: e.target.value})}
              placeholder="e.g. Production, Project, Asset"
              required
              className="bg-background border-border"
            />
          </div>

          <div className="space-y-2">
            <IconPicker
              value={formData.icon}
              onChange={(icon) => setFormData({...formData, icon})}
              label="Icon (Optional)"
            />
          </div>

          <div className="space-y-4 pt-4 border-t border-border">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-white">Role Overrides</h3>
              <p className="text-xs text-muted-foreground">
                Allow specific roles to use this label regardless of their company's industry type.
              </p>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-background rounded-lg border border-border">
              <Checkbox
                id="superadmin-override"
                checked={formData.superadmin_override}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, superadmin_override: checked as boolean })
                }
              />
              <div className="flex-1">
                <Label htmlFor="superadmin-override" className="cursor-pointer text-sm font-medium text-white">
                  Superadmin Override
                </Label>
                <p className="text-xs text-muted-foreground">
                  Superadmins can use this label for any company
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-background rounded-lg border border-border">
              <Checkbox
                id="admin-override"
                checked={formData.admin_override}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, admin_override: checked as boolean })
                }
              />
              <div className="flex-1">
                <Label htmlFor="admin-override" className="cursor-pointer text-sm font-medium text-white">
                  Admin Override
                </Label>
                <p className="text-xs text-muted-foreground">
                  Company admins can use this label regardless of industry
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]">
              {editingLabel ? 'Update Label' : 'Add Label'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>
    </div>
  );
}

